import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { companyInfo } from '../data/mock';

const WORKER_URL = "https://super-meadow-b127.arnabsarkarprofessional.workers.dev/";

// Google Maps URL for the office location
const GOOGLE_MAPS_URL = "https://www.google.com/maps/search/?api=1&query=155+Dumdum+Road+Ghoshpara+Kolkata+700074";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(
        WORKER_URL,
        {
          name: formData.name,
          email: formData.email,
          subject: `New contact from ${formData.name}`,
          phone: formData.phone,
          message: `
${formData.message}
          `
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data?.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEmail = () => {
    window.location.href = `mailto:${companyInfo.email}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  return (
    <section id="contact" className="contact-section" ref={ref}>
      <div className="contact-container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Contact Us
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Let's Build Something
            <span className="gradient-text"> Great Together</span>
          </motion.h2>
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Ready to transform your IT infrastructure? Get in touch with us.
          </motion.p>
        </motion.div>

        <div className="contact-content">
          {/* Contact Info Cards */}
          <motion.div 
            className="contact-info"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {/* Email Card */}
            <motion.div 
              className="info-card glass-card" 
              onClick={openEmail}
              variants={itemVariants}
              whileHover={{ x: 10, boxShadow: '0 15px 40px rgba(59, 130, 246, 0.15)' }}
              style={{ opacity: 1 }}
              data-testid="email-card"
            >
              <div className="info-icon">
                <Mail size={28} />
              </div>
              <div className="info-content">
                <h4>Email Us</h4>
                <p>{companyInfo.email}</p>
              </div>
              <div className="card-glow"></div>
            </motion.div>

            {/* Phone Cards */}
            {companyInfo.phones.map((phone, index) => (
              <motion.a 
                key={index} 
                href={`tel:${phone.replace(/\s/g, '')}`}
                className="info-card glass-card"
                variants={itemVariants}
                whileHover={{ x: 10, boxShadow: '0 15px 40px rgba(59, 130, 246, 0.15)' }}
                style={{ opacity: 1 }}
                data-testid={`phone-card-${index}`}
              >
                <div className="info-icon">
                  <Phone size={28} />
                </div>
                <div className="info-content">
                  <h4>Call Us</h4>
                  <p>{phone}</p>
                </div>
                <div className="card-glow"></div>
              </motion.a>
            ))}

            {/* Address Card */}
            <motion.a 
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="info-card glass-card address-card"
              variants={itemVariants}
              whileHover={{ x: 10, boxShadow: '0 15px 40px rgba(59, 130, 246, 0.15)' }}
              style={{ opacity: 1 }}
              data-testid="visit-us-link"
            >
              <div className="info-icon">
                <MapPin size={28} />
              </div>
              <div className="info-content">
                <h4>Visit Us <ExternalLink size={14} style={{ marginLeft: '6px', opacity: 0.7 }} /></h4>
                <p>{companyInfo.address}</p>
              </div>
              <div className="card-glow"></div>
            </motion.a>
          </motion.div>

          {/* Contact Form */}
          <motion.div 
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
          >
            <motion.form 
              className="contact-form glass-card" 
              onSubmit={handleSubmit}
              whileHover={{ boxShadow: '0 25px 60px rgba(59, 130, 246, 0.12)' }}
            >
              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div 
                    className="success-message"
                    key="success"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                    >
                      <CheckCircle size={60} />
                    </motion.div>
                    <h3>Message Sent!</h3>
                    <p>We'll get back to you soon.</p>
                  </motion.div>
                ) : submitStatus === 'error' ? (
                  <motion.div 
                    className="error-message"
                    key="error"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                    >
                      <AlertCircle size={60} />
                    </motion.div>
                    <h3>Something went wrong</h3>
                    <p>Please try again or contact us directly.</p>
                    <motion.button 
                      type="button" 
                      className="btn-secondary retry-btn"
                      onClick={() => setSubmitStatus(null)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Try Again
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Type your name here!"
                          required
                          minLength={2}
                          data-testid="name-input"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Your e-mail address here!"
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your mobile number here!"
                        data-testid="phone-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="message">Your Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project..."
                        rows={5}
                        required
                        minLength={10}
                        data-testid="message-input"
                      />
                    </div>
                    <motion.button 
                      type="submit" 
                      className="btn-primary submit-btn"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="submit-btn"
                    >
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                      {!isSubmitting && <Send size={20} />}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
